import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { PhotoIcon } from "@heroicons/react/24/solid";
import ArrowRightIcon from "@untitled-ui/icons-react/build/esm/ArrowRight";
import {
  Box,
  Button,
  Tabs,
  Container,
  Stack,
  SvgIcon,
  Typography,
  Chip,
  Link,
  Tab,
  Divider,
  Grid,
  Card,
  CardHeader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CardContent,
  DialogContent,
  Alert,
  Dialog,
  TextField,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { getAuth } from "firebase/auth";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { Scrollbar } from "../../components/scrollbar";
import { useParams, useNavigate } from "react-router-dom";
import { PropertyList } from "../../components/property-list";
import { PropertyListItem } from "../../components/property-list-item";

import ArrowLeftIcon from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import { PresencaParlamentares } from "../../sections/dashboard/account/presencaParlamentares";
import { sessao } from "../../@types/sessao";

type RoomParams = {
  id: string;
};

type transcription = {
  id: string;
  assemblyai_id: string;
  async: boolean;
  city: string;
  city_edit: string;
  createdAt: any;
  fast: boolean;
  sentences: Array<{
    speaker: string | null;
    confidence: number;
    end: number;
    start: number;
    text: string;
  }>;
  sessao: string;
  sessao_edit: string;
  startedAt: any;
  status: string;
  text: string;
  transcribedAt: any;
  updatedAt: any;
  url: string;
  video_id: string;
};

export function ResumoSessao() {
  const params = useParams<RoomParams>();
  const roomId = params.id;
  const navigate = useNavigate();

  const auth = getAuth();

  const [currentTab, setCurrentTab] = useState("details");
  const [filters, setFilters] = useState({});
  const [entidades, setEntidades] = useState<sessao>({} as sessao);

  const [loadingFetchApi, setLoadingFetchApi] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [pautas, setPautas] = useState<any[]>([]);

  const [open, setOnOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [pautasInfo, setPautasInfo] = useState<any[]>([]);
  const [pautasSessao, setPautasSessao] = useState<any[]>([]);

  const [transcription, setTranscription] = useState<transcription | null>(
    null
  );

  const [linkMidia, setLinkMidia] = useState("");
  const [transcriptionRequest, setTranscriptionRequest] = useState<any>(null);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);
  const [ataGerada, setAtaGerada] = useState<any>(null);
  const [loadingAta, setLoadingAta] = useState(false);

  type AtaGerada = {
    id: string;
    texto: string;
    criadoEm: any;
    modeloUsado: string;
    transcricaoId: string;
  };

  useEffect(() => {
    setFiles([]);
  }, [open]);

  useEffect(() => {
    if (!roomId) return;

    const db = getFirestore();
    const transcriptionsRef = collection(db, "transcriptions");

    const unsubscribe = onSnapshot(transcriptionsRef, (querySnapshot) => {
      // Buscar documentos onde o campo 'sessao' corresponde ao roomId
      const matchingDocs = querySnapshot.docs.filter((doc) => {
        const data = doc.data();
        return data.sessao === roomId;
      });

      if (matchingDocs.length > 0) {
        // Pegar o documento mais recente (último criado)
        const latestDoc = matchingDocs[matchingDocs.length - 1];
        const data = latestDoc.data();
        setTranscription({ id: latestDoc.id, ...data } as transcription);
        setTranscriptionRequest({ id: latestDoc.id, ...data });
      } else {
        console.log("Nenhuma transcrição encontrada para esta sessão");
        setTranscription(null);
        setTranscriptionRequest(null);
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  useEffect(() => {
    const db = getFirestore();

    const ordersRef = collection(
      db,
      "users",
      `belacruz@eleg`,
      "sessoes",
      `${roomId}`,
      "atas"
    );

    onSnapshot(ordersRef, (querySnapshot) => {
      const parsedData = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return { id: doc.id, ...data };
      });

      setPautasInfo(parsedData);
    });
  }, []);

  // Buscar atas geradas para esta sessão
  useEffect(() => {
    if (!roomId) return;

    setLoadingAta(true);
    const db = getFirestore();
    const atasRef = collection(
      db,
      "users",
      `belacruz@eleg`,
      "sessoes",
      `${roomId}`,
      "atasIA"
    );

    const unsubscribe = onSnapshot(atasRef, (querySnapshot) => {
      const atas = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (atas.length > 0) {
        // Pegar a ata mais recente
        const ataMaisRecente = atas.sort(
          (a, b) =>
            new Date(
              (b as AtaGerada).criadoEm?.toDate?.() ||
                (b as AtaGerada).criadoEm ||
                0
            ).getTime() -
            new Date(
              (a as AtaGerada).criadoEm?.toDate?.() ||
                (a as AtaGerada).criadoEm ||
                0
            ).getTime()
        )[0];
        setAtaGerada(ataMaisRecente);
      } else {
        setAtaGerada(null);
      }
      setLoadingAta(false);
    });

    return () => unsubscribe();
  }, [roomId]);

  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCurrentTab(value);
      setFilters((prevState) => {
        const updatedFilters = {
          ...prevState,
          hasAcceptedMarketing: undefined,
          isProspect: undefined,
          isReturning: undefined,
        };

        if (value !== "all") {
          (updatedFilters as any)[value] = true;
        }

        return updatedFilters;
      });
    },
    []
  );

  const tabs = [
    { label: "Detalhes", value: "details" },
    { label: "Mídias", value: "midias" },
    { label: "Pauta", value: "pauta" },
    { label: "Parlamentares", value: "parlamentares" },
    { label: "Proposições", value: "proposicoes" },
    { label: "Resultados", value: "resultados" },
  ];

  async function BuscaPaltas() {
    if (!roomId) {
      console.warn("roomId não encontrado, não é possível buscar pautas");
      return;
    }

    const db = getFirestore();
    const consulta1Snapshot = await getDocs(
      collection(db, "users", "belacruz@eleg", "sessoes", `${roomId}`, "pautas")
    );

    const resultadosAdicionais: any[] = [];

    // Realizar consultas adicionais para cada documento retornado pela primeira consulta
    await Promise.all(
      consulta1Snapshot.docs.map(async (docSnapshot) => {
        const docData = docSnapshot.data();
        const idPalta = docData?.idPalta;

        // Verificar se idPalta existe e é uma string válida
        if (!idPalta || typeof idPalta !== "string") {
          console.warn(
            "idPalta inválido ou ausente para documento:",
            docSnapshot.id
          );
          return;
        }

        try {
          const documentoRef = doc(
            db,
            "users",
            "belacruz@eleg",
            "pautas",
            idPalta
          );
          const consultaAdicionalSnapshot = await getDoc(documentoRef);

          if (consultaAdicionalSnapshot.exists()) {
            resultadosAdicionais.push(consultaAdicionalSnapshot.data());
          } else {
            console.warn("Documento não encontrado para idPalta:", idPalta);
          }
        } catch (error) {
          console.error(
            "Erro ao buscar documento para idPalta:",
            idPalta,
            error
          );
        }
      })
    );

    setPautasSessao(resultadosAdicionais);
  }

  async function solicitarTranscricao(videoUrl: string) {
    const db = getFirestore();

    try {
      const docRef = await addDoc(collection(db, "transcriptions"), {
        url: videoUrl,
        async: true,
        fast: false,
        city: "belacruz",
        sessao: roomId || "Sessão não identificada",
        status: "pending",
        createdAt: Timestamp.now(),
      });

      console.log("Requisição criada:", docRef.id);

      return docRef.id;
    } catch (error) {
      console.error("Erro ao criar requisição de transcrição:", error);
      throw error;
    }
  }

  // Função para limpar requisição (opcional)
  const limparRequisicao = () => {
    setTranscriptionRequest(null);
  };

  // Função para extrair ID do YouTube
  const getYouTubeVideoId = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Função para detectar a plataforma do vídeo
  const getVideoPlatform = (url: string) => {
    if (!url) return "unknown";

    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      return "youtube";
    } else if (url.includes("facebook.com") || url.includes("fb.watch")) {
      return "facebook";
    } else if (url.includes("vimeo.com")) {
      return "vimeo";
    } else if (url.includes("dailymotion.com")) {
      return "dailymotion";
    }

    return "unknown";
  };

  // Função para obter o ícone da plataforma
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "youtube":
        return (
          <svg
            className="w-5 h-5 text-red-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
        );
      case "facebook":
        return (
          <svg
            className="w-5 h-5 text-blue-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        );
      case "vimeo":
        return (
          <svg
            className="w-5 h-5 text-blue-500"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z" />
          </svg>
        );
      case "dailymotion":
        return (
          <svg
            className="w-5 h-5 text-blue-400"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        );
      default:
        return (
          <svg
            className="w-5 h-5 text-gray-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        );
    }
  };

  useEffect(() => {
    const db = getFirestore();

    const ordersRef = doc(db, "users", "belacruz@eleg", "sessoes", `${roomId}`);

    getDoc(ordersRef)
      .then((docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const obj = {
            id: docSnapshot.id,
            sessao: data.sessao,
            legislatura: data.legislatura,
            dataSessao: data.dataSessao,
            dataSessaoVis: format(
              new Date(data.dataSessao),
              "d 'de' MMMM 'de' yyyy",
              { locale: ptBR }
            ),
            tipoSessao: data.tipoSessao,
            statusSessao: data.statusSessao,
            periodo: data.periodo,
            statusConfigSessao: data.statusConfigSessao,
          };
          setEntidades(obj);
        } else {
          console.log("Documento não encontrado");
        }
      })
      .catch((error) => {
        console.log("Erro ao recuperar dados do documento:", error);
      });

    const ordersRefAtas = collection(
      db,
      "users",
      `belacruz@eleg`,
      "sessoes",
      `${roomId}`,
      "pautas"
    );

    getDocs(ordersRefAtas)
      .then((querySnapshot) => {
        const parsedData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          // Cria um objeto com as propriedades e seus respectivos valores
          const obj = {
            id: doc.id,
            ...data,
            // Adicione outras propriedades e seus valores aqui, se necessário
          };
          return obj;
        });

        setPautas(parsedData);
      })
      .catch((error) => {
        console.log("Erro ao recuperar dados da subcoleção:", error);
      });

    if (roomId) {
      BuscaPaltas();
    }
  }, []);

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 4,
      }}
    >
      <Dialog fullWidth maxWidth="xs" open={open}>
        <DialogContent>
          <Stack
            sx={{
              width: "100%",
            }}
          >
            <Alert severity="warning">
              Não é possível reverter esta ação. Tem certeza?
            </Alert>

            <Stack
              sx={{
                width: "100%",
                alignSelf: "flex-end",
                marginTop: 0,
              }}
              justifyContent="flex-end"
              alignItems="flex-end"
              spacing={2}
              flexDirection="row"
            >
              <Button
                color="error"
                onClick={async () => {
                  const db = getFirestore();
                  await deleteDoc(
                    doc(db, "users", "belacruz@eleg", "sessoes", `${roomId}`)
                  );
                  navigate("/sessoes");
                  setOnOpen(false);
                }}
              >
                Apagar mesmo assim
              </Button>
              <Button
                color="inherit"
                onClick={() => {
                  setOnOpen(false);
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <Container maxWidth="xl">
        <Stack spacing={4}>
          <Stack spacing={4}>
            <div>
              <NavLink to="/sessoes">
                <Link
                  color="text.primary"
                  sx={{
                    alignItems: "center",
                    display: "inline-flex",
                  }}
                  underline="hover"
                >
                  <SvgIcon sx={{ mr: 1 }}>
                    <ArrowLeftIcon />
                  </SvgIcon>
                  <Typography variant="subtitle2">Voltar</Typography>
                </Link>
              </NavLink>
            </div>

            <Stack
              alignItems="flex-start"
              direction={{
                xs: "column",
                md: "row",
              }}
              justifyContent="space-between"
              spacing={4}
            >
              <Stack alignItems="center" direction="row" spacing={2}>
                <Stack spacing={1}>
                  <Typography variant="h4">
                    Sessão {entidades?.sessao || "Carregando..."}
                  </Typography>
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <Chip
                      label={`${
                        (entidades?.legislatura === "2025-2028" &&
                          "Legislatura (2025) - (2028)") ||
                        (entidades?.legislatura === "2021-2024" &&
                          "Legislatura (2021) - (2024)") ||
                        (entidades?.legislatura === "" && "Não informado")
                      }`}
                      size="small"
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <div>
              <Tabs
                indicatorColor="primary"
                onChange={handleTabsChange}
                scrollButtons="auto"
                sx={{ mt: 3 }}
                textColor="primary"
                value={currentTab}
                variant="scrollable"
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                    sx={{
                      paddingX: 6.3,
                    }}
                  />
                ))}
              </Tabs>
              <Divider />
            </div>
          </Stack>

          {currentTab === "details" && (
            <div>
              <Grid spacing={4}>
                <Grid xs={12} lg={3.75}>
                  <Card>
                    <CardHeader title="Resumo da Sessão" />
                    <CardContent sx={{ pt: 0 }}>
                      <Grid container justifyContent={"space-between"}>
                        <Grid xs={12} sm={5.85}>
                          <Card sx={{ height: "100%" }} variant="outlined">
                            <CardContent>
                              <PropertyList>
                                <PropertyListItem
                                  label="Legislatura"
                                  value={
                                    (entidades?.legislatura === "2025-2028" &&
                                      "Legislatura (2025) - (2028)") ||
                                    (entidades?.legislatura === "2021-2024" &&
                                      "Legislatura (2021) - (2024)") ||
                                    (entidades?.legislatura === "" &&
                                      "Não informado") ||
                                    "Não informado"
                                  }
                                />
                                <PropertyListItem
                                  label="Sessão"
                                  value={entidades?.sessao || "Carregando..."}
                                />
                                <PropertyListItem
                                  label="Tipo Sessão"
                                  value={
                                    entidades?.tipoSessao === "extraordinaria"
                                      ? "Extraordinária"
                                      : "Ordinária"
                                  }
                                />
                                <PropertyListItem
                                  label="Data da Sessão"
                                  value={
                                    entidades?.dataSessao
                                      ? format(
                                          new Date(entidades.dataSessao),
                                          "d 'de' MMMM 'de' yyyy",
                                          { locale: ptBR }
                                        )
                                      : "Carregando..."
                                  }
                                />

                                <PropertyListItem
                                  label="Status"
                                  value={
                                    entidades?.statusSessao === "fechada"
                                      ? "ENCERRADA"
                                      : null
                                  }
                                />
                              </PropertyList>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </div>
          )}
          {currentTab === "midias" && (
            <div>
              <Grid spacing={4}>
                <Grid xs={12} lg={3.75}>
                  <Grid container justifyContent={"space-between"}>
                    <Grid sm={5.85} xs={12}>
                      <Card
                        sx={{
                          height: "100%",
                          background:
                            "linear-gradient(135deg, #fefefe 0%, #f8fafc 100%)",
                          border: "1px solid #e2e8f0",
                          borderRadius: "16px",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        }}
                        variant="outlined"
                      >
                        <CardContent sx={{ p: 3 }}>
                          {/* Status indicator */}
                          <div className="flex items-center mb-4">
                            <div className="relative mr-3">
                              <div
                                className={`w-3 h-3 rounded-full ${
                                  transcription?.status === "completed"
                                    ? "bg-green-500 animate-pulse"
                                    : transcriptionRequest?.status ===
                                        "processing" ||
                                      transcription?.status === "processing"
                                    ? "bg-yellow-500 animate-pulse"
                                    : transcriptionRequest?.status === "pending"
                                    ? "bg-blue-500 animate-pulse"
                                    : "bg-red-500"
                                }`}
                              ></div>
                              {transcription?.status === "completed" && (
                                <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75"></div>
                              )}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">
                                {transcription?.status === "completed"
                                  ? "Vídeo Processado"
                                  : transcriptionRequest?.status ===
                                      "processing" ||
                                    transcription?.status === "processing"
                                  ? "Processando Vídeo"
                                  : transcriptionRequest?.status === "pending"
                                  ? "Aguardando Processamento"
                                  : "Sem Mídia"}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {transcription?.status === "completed"
                                  ? "Transcrição finalizada com sucesso"
                                  : transcriptionRequest?.status ===
                                      "processing" ||
                                    transcription?.status === "processing"
                                  ? "Processamento em andamento..."
                                  : transcriptionRequest?.status === "pending"
                                  ? "Sua requisição foi enviada"
                                  : "Adicione um link de mídia para começar"}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <svg
                                  className="w-5 h-5 text-blue-600"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                                <span className="text-sm font-semibold text-blue-800">
                                  Geração Automática de Ata
                                </span>
                              </div>
                              <p className="text-sm text-blue-700 leading-relaxed">
                                Será usado para a geração automática da ata da
                                sessão, com inteligência artificial
                              </p>
                            </div>

                            {transcription?.status === "completed" &&
                              !ataGerada && (
                                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <svg
                                      className="w-5 h-5 text-green-600"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-sm font-semibold text-green-800">
                                      Pronto para Revisão
                                    </span>
                                  </div>
                                  <p className="text-sm text-green-700 leading-relaxed mb-3">
                                    Com o vídeo processado, você pode revisar e
                                    corrigir alguns erros de ortografia e
                                    gramática que podem acontecer na transcrição
                                    do vídeo.
                                  </p>
                                  <div className="flex items-center justify-between">
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/sessoes/${roomId}/resumo/midia`
                                        )
                                      }
                                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors duration-200 shadow-sm"
                                    >
                                      <svg
                                        className="w-4 h-4 mr-2"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                      Ir para a Transcrição
                                    </button>
                                    <div className="text-right">
                                      <p className="text-xs text-green-600 font-medium">
                                        Finalizada em
                                      </p>
                                      <p className="text-xs text-green-700 font-semibold">
                                        {transcription?.transcribedAt
                                          ? format(
                                              transcription.transcribedAt.toDate(),
                                              "d 'de' MMMM',' HH:mm",
                                              { locale: ptBR }
                                            )
                                          : "Data não disponível"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* Visualização quando a ata já foi gerada */}
                            {transcription?.status === "completed" &&
                              ataGerada && (
                                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <svg
                                      className="w-5 h-5 text-purple-600"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                                    </svg>
                                    <span className="text-sm font-semibold text-purple-800">
                                      Ata Gerada com Sucesso
                                    </span>
                                  </div>
                                  <p className="text-sm text-purple-700 leading-relaxed mb-3">
                                    A ata desta sessão foi gerada
                                    automaticamente e está pronta para uso.
                                  </p>
                                  <div className="space-y-2 mb-3">
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-purple-600 font-medium">
                                        Gerada em:
                                      </span>
                                      <span className="text-xs text-purple-800 font-semibold">
                                        {ataGerada.criadoEm
                                          ? format(
                                              ataGerada.criadoEm.toDate?.() ||
                                                new Date(ataGerada.criadoEm),
                                              "d 'de' MMMM 'de' yyyy 'às' HH:mm",
                                              { locale: ptBR }
                                            )
                                          : "Data não disponível"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-purple-600 font-medium">
                                        Modelo usado:
                                      </span>
                                      <span className="text-xs text-purple-800 font-semibold">
                                        {ataGerada.modeloUsado ||
                                          "Modelo padrão"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/sessoes/${roomId}/resumo/midia`
                                        )
                                      }
                                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors duration-200"
                                    >
                                      <svg
                                        className="w-4 h-4 mr-2"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                      Editar Transcrição
                                    </button>
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/sessoes/${roomId}/resumo/midia/confirmar-ata`
                                        )
                                      }
                                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors duration-200"
                                    >
                                      <svg
                                        className="w-4 h-4 mr-2"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                                      </svg>
                                      Ver Ata Completa
                                    </button>
                                  </div>
                                </div>
                              )}

                            {/* Informações da requisição em andamento */}
                            {transcriptionRequest && !transcription?.status && (
                              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <div className="flex items-center space-x-2 mb-3">
                                  <svg
                                    className="w-5 h-5 text-blue-600"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                  </svg>
                                  <span className="text-sm font-semibold text-blue-800">
                                    Processamento em Andamento
                                  </span>
                                </div>
                                <p className="text-sm text-blue-700 leading-relaxed mb-3">
                                  {transcriptionRequest.status === "pending"
                                    ? "Sua requisição foi enviada e está aguardando processamento..."
                                    : transcriptionRequest.status ===
                                      "processing"
                                    ? "O vídeo está sendo processado. Isso pode levar alguns minutos..."
                                    : "Processando sua requisição..."}
                                </p>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-blue-600 font-medium">
                                      ID da Requisição:
                                    </span>
                                    <span className="text-xs text-blue-800 font-mono bg-blue-100 px-2 py-1 rounded">
                                      {transcriptionRequest.id}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-blue-600 font-medium">
                                      Criada em:
                                    </span>
                                    <span className="text-xs text-blue-800 font-semibold">
                                      {transcriptionRequest.createdAt
                                        ? format(
                                            transcriptionRequest.createdAt.toDate(),
                                            "d 'de' MMMM',' HH:mm",
                                            { locale: ptBR }
                                          )
                                        : "Data não disponível"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Formulário para adicionar mídia */}
                            {!transcription?.status &&
                              !transcriptionRequest && (
                                <div className="space-y-4">
                                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                                    <div className="flex items-center space-x-2 mb-2">
                                      <svg
                                        className="w-5 h-5 text-purple-600"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                      </svg>
                                      <span className="text-sm font-semibold text-purple-800">
                                        Integração com I.A
                                      </span>
                                    </div>
                                    <p className="text-sm text-purple-700 leading-relaxed">
                                      Vamos fazer a transcrição do vídeo e criar
                                      uma ata automática.
                                    </p>
                                  </div>

                                  <TextField
                                    value={linkMidia}
                                    error={linkMidia === "" ? true : false}
                                    onChange={(e) =>
                                      setLinkMidia(e.target.value)
                                    }
                                    label="Link da transmissão (YouTube, Facebook)"
                                    fullWidth
                                    helperText="Cole o link do vídeo que deseja transcrever"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                      },
                                    }}
                                  />

                                  <Button
                                    color="primary"
                                    variant="contained"
                                    size="large"
                                    startIcon={
                                      loadingFetchApi ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <svg
                                          className="w-5 h-5"
                                          fill="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path d="M8 5v14l11-7z" />
                                        </svg>
                                      )
                                    }
                                    onClick={async () => {
                                      setLoadingFetchApi(true);
                                      setErrorMessage("");

                                      if (!roomId) {
                                        setErrorMessage(
                                          "ID da sessão não encontrado. Recarregue a página e tente novamente."
                                        );
                                        setLoadingFetchApi(false);
                                        return;
                                      }

                                      if (
                                        linkMidia &&
                                        linkMidia.trim() !== ""
                                      ) {
                                        const urlPattern =
                                          /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|(.*\.)?facebook\.com|fb\.watch|vimeo\.com|dailymotion\.com)/i;

                                        if (
                                          !linkMidia ||
                                          typeof linkMidia !== "string" ||
                                          !urlPattern.test(linkMidia)
                                        ) {
                                          setErrorMessage(
                                            "Por favor, insira um link válido do YouTube, Facebook, Vimeo ou Dailymotion."
                                          );
                                          setLoadingFetchApi(false);
                                          return;
                                        }

                                        try {
                                          const requestId =
                                            await solicitarTranscricao(
                                              linkMidia.trim()
                                            );
                                          console.log(
                                            "Requisição de transcrição criada com ID:",
                                            requestId
                                          );

                                          setLinkMidia("");
                                          setErrorMessage("");

                                          console.log(
                                            "Transcrição solicitada com sucesso!"
                                          );
                                        } catch (error) {
                                          console.error(
                                            "Erro ao solicitar transcrição:",
                                            error
                                          );
                                          setErrorMessage(
                                            "Erro ao criar requisição de transcrição. Tente novamente."
                                          );
                                        } finally {
                                          setLoadingFetchApi(false);
                                        }
                                      } else {
                                        setErrorMessage(
                                          "Por favor, insira um link de vídeo."
                                        );
                                        setLoadingFetchApi(false);
                                      }
                                    }}
                                    disabled={loadingFetchApi}
                                    fullWidth
                                    sx={{
                                      borderRadius: "12px",
                                      py: 1.5,
                                      fontSize: "0.95rem",
                                      fontWeight: 600,
                                      textTransform: "none",
                                      boxShadow:
                                        "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                      "&:hover": {
                                        boxShadow:
                                          "0 6px 8px -1px rgba(0, 0, 0, 0.15)",
                                      },
                                    }}
                                  >
                                    {loadingFetchApi
                                      ? "Iniciando Transcrição..."
                                      : "Iniciar Transcrição"}
                                  </Button>
                                </div>
                              )}

                            {errorMessage && (
                              <Alert
                                severity="error"
                                sx={{
                                  mt: 2,
                                  borderRadius: "12px",
                                  "& .MuiAlert-message": {
                                    fontSize: "0.875rem",
                                  },
                                }}
                                onClose={() => setErrorMessage("")}
                                action={
                                  <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => setErrorMessage("")}
                                  >
                                    Fechar
                                  </Button>
                                }
                              >
                                {errorMessage}
                              </Alert>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Grid>

                    {transcription?.status === "completed" && (
                      <Grid sm={5.85} xs={12}>
                        <Card
                          sx={{
                            height: "100%",
                            background:
                              "linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%)",
                            border: "1px solid #dcfce7",
                            borderRadius: "16px",
                            boxShadow:
                              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            {/* Header com status */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <div className="relative">
                                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                  <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75"></div>
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-800">
                                    Transcrição Concluída
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    Processamento finalizado com sucesso
                                  </p>
                                </div>
                              </div>
                              <div className="bg-green-100 px-3 py-1 rounded-full">
                                <span className="text-xs font-medium text-green-800">
                                  ✓ Concluído
                                </span>
                              </div>
                            </div>

                            {/* Informações principais */}
                            <div className="space-y-3 mb-4">
                              <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3 border border-green-200">
                                <div className="flex items-center space-x-2 mb-2">
                                  <svg
                                    className="w-4 h-4 text-blue-600"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                  </svg>
                                  <span className="text-sm font-medium text-gray-700">
                                    ID da Transcrição
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded">
                                  {transcription?.assemblyai_id}
                                </p>
                              </div>

                              {transcription?.transcribedAt && (
                                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3 border border-green-200">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <svg
                                      className="w-4 h-4 text-purple-600"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                    <span className="text-sm font-medium text-gray-700">
                                      Concluída em
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600">
                                    {format(
                                      transcription.transcribedAt.toDate(),
                                      "d 'de' MMMM 'de' yyyy 'às' HH:mm",
                                      { locale: ptBR }
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Seção do vídeo */}
                            {transcription?.url && (
                              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-2">
                                    {getPlatformIcon(
                                      getVideoPlatform(transcription.url)
                                    )}
                                    <span className="text-sm font-semibold text-gray-800">
                                      Vídeo Original
                                    </span>
                                  </div>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setShowMiniPlayer(!showMiniPlayer)
                                    }
                                    sx={{
                                      color: showMiniPlayer
                                        ? "#1976d2"
                                        : "#666",
                                      backgroundColor: showMiniPlayer
                                        ? "#e3f2fd"
                                        : "#f5f5f5",
                                      "&:hover": {
                                        backgroundColor: showMiniPlayer
                                          ? "#bbdefb"
                                          : "#e0e0e0",
                                      },
                                      transition: "all 0.2s ease-in-out",
                                    }}
                                  >
                                    {showMiniPlayer ? (
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                      </svg>
                                    ) : (
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    )}
                                  </IconButton>
                                </div>

                                {showMiniPlayer &&
                                  getYouTubeVideoId(transcription.url) && (
                                    <div className="mb-4">
                                      <div
                                        className="relative w-full rounded-lg overflow-hidden shadow-lg"
                                        style={{ paddingBottom: "56.25%" }}
                                      >
                                        <iframe
                                          className="absolute top-0 left-0 w-full h-full"
                                          src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                                            transcription.url
                                          )}?autoplay=0&rel=0&modestbranding=1`}
                                          title="Vídeo da Sessão"
                                          frameBorder="0"
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                          allowFullScreen
                                        />
                                      </div>
                                    </div>
                                  )}

                                <div className="space-y-2">
                                  <a
                                    href={transcription.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors duration-200 group"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-blue-600 group-hover:text-blue-800 font-medium truncate">
                                          {transcription.url}
                                        </p>
                                        {transcription?.video_id && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            ID: {transcription.video_id}
                                          </p>
                                        )}
                                      </div>
                                      <svg
                                        className="w-4 h-4 text-gray-400 group-hover:text-gray-600 ml-2 flex-shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    </div>
                                  </a>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </div>
          )}
          {currentTab === "pauta" && (
            <div>
              <Grid spacing={4}>
                <Grid xs={12} lg={3.75}>
                  <div className="col-span-full">
                    <label
                      htmlFor="cover-photo"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Adicione o arquivo de pauta
                    </label>
                    <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                      <div className="text-center">
                        <PhotoIcon
                          className="mx-auto h-12 w-12 text-gray-300"
                          aria-hidden="true"
                        />
                        <div className="mt-4 flex text-sm leading-6 text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer rounded-md bg-white font-semibold text-[#2970FF] focus-within:outline-none focus-within:ring-2 focus-within:[#2970FF] focus-within:ring-offset-2 hover:text-[#2970FF]"
                          >
                            <span>Enviar arquivo</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              className="sr-only"
                            />
                          </label>
                          <p className="pl-1">ou arraste aqui</p>
                        </div>
                        <p className="text-xs leading-5 text-gray-600">
                          PDF de no maxímo 10MB
                        </p>
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
          )}
          {currentTab === "parlamentares" && (
            <div>
              <Grid spacing={4}>
                <Grid xs={12} lg={3.75}>
                  <PresencaParlamentares />
                </Grid>
              </Grid>
            </div>
          )}

          {currentTab === "proposicoes" && (
            <Card>
              <CardHeader title="Proposições" />

              <Scrollbar>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Texto da Ata</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pautas !== undefined &&
                      pautas.map((item) => {
                        function removeTags(textoHTML: string | undefined) {
                          if (!textoHTML) return "";
                          const div = document.createElement("div");
                          div.innerHTML = textoHTML;
                          return div.textContent || div.innerText || "";
                        }

                        return (
                          <TableRow hover key={(item as any).id}>
                            <TableCell>
                              <Typography variant="subtitle1">
                                {(item as any).numero}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1">
                                {(item as any).pautaLegislativa}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {removeTags((item as any).expressoes)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </Scrollbar>
            </Card>
          )}
          {currentTab === "pautasLegislativas" && (
            <Card>
              <CardHeader title="Pautas Legislativas Cadastradas" />

              <Scrollbar>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Título</TableCell>
                      <TableCell>Texto da Ata</TableCell>
                      <TableCell>Tipo da pauta legislativa</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pautasSessao !== undefined &&
                      pautasSessao.map((item) => {
                        return (
                          <TableRow hover key={(item as any).id}>
                            <TableCell>
                              <Typography variant="subtitle1">
                                {(item as any).numero}
                              </Typography>
                            </TableCell>
                            <TableCell>{(item as any).expressoes}</TableCell>
                            <TableCell>
                              {(item as any).pautaLegislativa}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </Scrollbar>
            </Card>
          )}
          {currentTab === "resultados" && (
            <>
              {pautasInfo.map((pauta, index) => (
                <Card key={index}>
                  <CardHeader title="Emails" />
                  <CardContent sx={{ pt: 0 }}>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        endIcon={
                          <SvgIcon>
                            <ArrowRightIcon />
                          </SvgIcon>
                        }
                        variant="contained"
                      >
                        Send email
                      </Button>
                    </Box>
                  </CardContent>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mail Type</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody></TableBody>
                  </Table>
                </Card>
              ))}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
